import React, { useState, useCallback, useEffect } from 'react';
import Header from './Header';
import Card from './Card';
import PayoutTable from './PayoutTable';
import MoneyPanel from './MoneyPanel';
import SettingsPanel, { ChangeDeckModal } from './SettingsPanel';
import RecentBigWins from './RecentBigWins';
import StatsModal from './StatsModal';
import ResetMoneyModal from './ResetMoneyModal';
import AuthModal from './AuthModal';
import MobileMenu from './MobileMenu';
import SEOContent from './SEOContent';
import { TRIPS_PAYOUT, BLIND_PAYOUT, DECK, INITIAL_MONEY } from '../data/mockData';
import { shuffleDeck, getBestHand, calculateHandResults, evaluatePreflopHand, compareHands } from '../utils/gameLogic';
import { useLanguage } from '../i18n/LanguageContext';

const GAME_PHASES = {
  BETTING: 'betting',
  PREFLOP: 'preflop',
  FLOP: 'flop',
  TURN_RIVER: 'turn_river',
  SHOWDOWN: 'showdown',
  ANIMATING_FLOP: 'animating_flop',
  ANIMATING_TURN: 'animating_turn',
  ANIMATING_RIVER: 'animating_river',
  ANIMATING_DEALER: 'animating_dealer',
};

// Mobile-optimized Chip component with +/- buttons for editable chips
const MobileChip = ({ label, value, onChange, color, disabled, result, size = 'normal', t }) => {
  const colors = {
    gold: { bg: 'linear-gradient(145deg, #ffd700, #b8860b)', border: '#ffd700' },
    red: { bg: 'linear-gradient(145deg, #dc2626, #991b1b)', border: '#ef4444' },
    blue: { bg: 'linear-gradient(145deg, #2563eb, #1d4ed8)', border: '#3b82f6' },
    green: { bg: 'linear-gradient(145deg, #16a34a, #15803d)', border: '#22c55e' },
    purple: { bg: 'linear-gradient(145deg, #9333ea, #7e22ce)', border: '#a855f7' }
  };
  const style = colors[color] || colors.gold;
  const sizeClass = size === 'small' ? 'w-14 h-14 md:w-20 md:h-20' : 'w-16 h-16 md:w-24 md:h-24';
  const isEditable = onChange && !disabled;

  const increment = (amount) => {
    if (onChange && !disabled) {
      onChange(Math.max(0, value + amount));
    }
  };

  return (
    <div className="flex flex-col items-center">
      {/* Plus button - only show when editable */}
      {isEditable && (
        <button 
          onClick={() => increment(5)}
          className="mb-1 w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center font-bold text-sm md:text-lg transition-all hover:scale-110 active:scale-95"
          style={{ 
            background: 'linear-gradient(145deg, #22c55e, #16a34a)', 
            color: 'white',
            boxShadow: '0 2px 8px rgba(34,197,94,0.4)'
          }}
        >
          +
        </button>
      )}
      
      <div 
        className={`${sizeClass} rounded-full flex items-center justify-center relative ${isEditable ? 'cursor-pointer hover:scale-105 transition-transform' : ''}`}
        style={{
          background: style.bg,
          border: `3px solid ${style.border}`,
          boxShadow: isEditable 
            ? `0 4px 12px rgba(0,0,0,0.4), inset 0 1px 2px rgba(255,255,255,0.3), 0 0 15px ${style.border}40`
            : `0 4px 12px rgba(0,0,0,0.4), inset 0 1px 2px rgba(255,255,255,0.3)`
        }}
      >
        <div className="absolute inset-1 md:inset-2 rounded-full border-2 border-dashed opacity-30" style={{ borderColor: 'rgba(255,255,255,0.5)' }}></div>
        
        {/* Editable indicator pulse */}
        {isEditable && (
          <div className="absolute inset-0 rounded-full animate-pulse" style={{ 
            border: `2px solid ${style.border}`,
            opacity: 0.5
          }}></div>
        )}
        
        <div className="text-center z-10">
          <div className="text-[8px] md:text-xs font-bold text-white/80 uppercase">{label}</div>
          <div className="flex items-center justify-center">
            <span className="text-white/60 text-[10px] md:text-sm">$</span>
            {onChange ? (
              <input
                type="number"
                value={value}
                onChange={(e) => onChange(Math.max(0, parseInt(e.target.value) || 0))}
                disabled={disabled}
                className="w-8 md:w-12 text-center text-sm md:text-lg font-black bg-transparent text-white border-none outline-none cursor-pointer"
                style={{ caretColor: '#ffd700' }}
                min="0"
              />
            ) : (
              <span className="text-sm md:text-lg font-black text-white">{value}</span>
            )}
          </div>
        </div>
      </div>
      
      {/* Minus button - only show when editable */}
      {isEditable && (
        <button 
          onClick={() => increment(-5)}
          className="mt-1 w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center font-bold text-sm md:text-lg transition-all hover:scale-110 active:scale-95"
          style={{ 
            background: 'linear-gradient(145deg, #ef4444, #dc2626)', 
            color: 'white',
            boxShadow: '0 2px 8px rgba(239,68,68,0.4)'
          }}
        >
          −
        </button>
      )}
      
      {/* "Click to edit" hint - show only on desktop when editable and no result */}
      {isEditable && !result && (
        <div className="hidden md:block mt-1 text-[9px] font-medium animate-pulse" style={{ color: style.border }}>
          ✏️ {t ? t('clickToEdit') : 'Click to edit'}
        </div>
      )}
      
      {result && (
        <div className={`mt-1 px-2 py-0.5 rounded-full text-[10px] md:text-xs font-bold ${
          result.includes('WIN') ? 'bg-green-500/20 text-green-400' : 
          result === 'PUSH' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'
        }`}>
          {result}
        </div>
      )}
    </div>
  );
};

const GameBoard = () => {
  const { t } = useLanguage();
  
  // Game State
  const [money, setMoney] = useState(INITIAL_MONEY);
  const [communityCards, setCommunityCards] = useState([]);
  const [playerHand, setPlayerHand] = useState([]);
  const [dealerHand, setDealerHand] = useState([]);
  const [gamePhase, setGamePhase] = useState(GAME_PHASES.BETTING);
  
  // Animation state
  const [visibleCommunityCount, setVisibleCommunityCount] = useState(0);
  const [showDealerCardsAnim, setShowDealerCardsAnim] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationMessage, setAnimationMessage] = useState('');
  
  // Betting State
  const [anteBet, setAnteBet] = useState(25);
  const [blindBet, setBlindBet] = useState(25);
  const [tripsBet, setTripsBet] = useState(0);
  const [playBet, setPlayBet] = useState(0);
  const [hasRaised, setHasRaised] = useState(false);
  
  // Game results
  const [gameResults, setGameResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  
  // Stats
  const [handsPlayed, setHandsPlayed] = useState(0);
  const [stats, setStats] = useState({
    handsPlayed: 0, winPercent: 0, losePercent: 0, foldPercent: 0,
    winnings: 0, wins: 0, losses: 0, folds: 0, goodFolds: 0,
    // By bet type
    bets4x: 0, bets3x: 0, bets2x: 0, bets1x: 0,
    wins4x: 0, wins3x: 0, wins2x: 0, wins1x: 0,
    losses4x: 0, losses3x: 0, losses2x: 0, losses1x: 0,
    // Trips & Blind bonuses
    tripsWins: 0, tripsWinnings: 0,
    blindBonuses: 0, blindBonusWinnings: 0,
    // By hand type
    royalFlushWins: 0, royalFlushLosses: 0,
    straightFlushWins: 0, straightFlushLosses: 0,
    fourKindWins: 0, fourKindLosses: 0,
    fullHouseWins: 0, fullHouseLosses: 0,
    flushWins: 0, flushLosses: 0,
    straightWins: 0, straightLosses: 0,
    threeKindWins: 0, threeKindLosses: 0,
    twoPairWins: 0, twoPairLosses: 0,
    pairWins: 0, pairLosses: 0,
    highCardWins: 0, highCardLosses: 0,
    bestHand: null,
  });
  const [currentBetType, setCurrentBetType] = useState(null); // '4x', '3x', '2x', '1x'
  
  // Settings & Modals
  const [enableAdvice, setEnableAdvice] = useState(false);
  const [enableSound, setEnableSound] = useState(false);
  const [selectedDeck, setSelectedDeck] = useState('original');
  const [showStats, setShowStats] = useState(false);
  const [showResetMoney, setShowResetMoney] = useState(false);
  const [showChangeDeck, setShowChangeDeck] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  
  const [playersOnline] = useState(Math.floor(Math.random() * 100) + 150);

  const getTotalBet = () => anteBet + blindBet + tripsBet + playBet;

  // Deal cards
  const dealCards = useCallback(() => {
    const totalInitialBet = anteBet + blindBet + tripsBet;
    if (anteBet <= 0) { alert(t('notEnoughMoney')); return; }
    if (money < totalInitialBet) { alert(t('notEnoughMoney')); return; }

    const shuffled = shuffleDeck([...DECK]);
    setPlayerHand([shuffled[0], shuffled[1]]);
    setDealerHand([shuffled[2], shuffled[3]]);
    setCommunityCards([shuffled[4], shuffled[5], shuffled[6], shuffled[7], shuffled[8]]);
    
    setMoney(prev => prev - totalInitialBet);
    setPlayBet(0);
    setHasRaised(false);
    setVisibleCommunityCount(0);
    setShowDealerCardsAnim(false);
    setIsAnimating(false);
    setAnimationMessage('');
    setGamePhase(GAME_PHASES.PREFLOP);
    setShowResults(false);
    setGameResults(null);
  }, [anteBet, blindBet, tripsBet, money, t]);

  // Animate card reveal
  const runCardAnimation = useCallback(async (startFrom = 'flop') => {
    setIsAnimating(true);
    const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
    
    if (startFrom === 'flop') {
      setAnimationMessage('FLOP');
      setGamePhase(GAME_PHASES.ANIMATING_FLOP);
      await delay(300);
      setVisibleCommunityCount(1);
      await delay(200);
      setVisibleCommunityCount(2);
      await delay(200);
      setVisibleCommunityCount(3);
      await delay(500);
    }
    
    if (startFrom === 'flop' || startFrom === 'turn') {
      setAnimationMessage('TURN');
      setGamePhase(GAME_PHASES.ANIMATING_TURN);
      await delay(300);
      setVisibleCommunityCount(4);
      await delay(500);
    }
    
    setAnimationMessage('RIVER');
    setGamePhase(GAME_PHASES.ANIMATING_RIVER);
    await delay(300);
    setVisibleCommunityCount(5);
    await delay(500);
    
    setAnimationMessage('SHOWDOWN');
    setGamePhase(GAME_PHASES.ANIMATING_DEALER);
    await delay(300);
    setShowDealerCardsAnim(true);
    await delay(800);
    
    setAnimationMessage('');
    setIsAnimating(false);
    setGamePhase(GAME_PHASES.SHOWDOWN);
  }, []);

  const makePlayBet = useCallback((multiplier) => {
    const bet = anteBet * multiplier;
    if (money < bet) { alert(t('notEnoughMoney')); return; }
    
    setPlayBet(bet);
    setMoney(prev => prev - bet);
    setHasRaised(true);
    setCurrentBetType(`${multiplier}x`);
    
    if (gamePhase === GAME_PHASES.PREFLOP) runCardAnimation('flop');
    else if (gamePhase === GAME_PHASES.FLOP) runCardAnimation('turn');
    else if (gamePhase === GAME_PHASES.TURN_RIVER) {
      setShowDealerCardsAnim(true);
      setGamePhase(GAME_PHASES.SHOWDOWN);
    }
  }, [anteBet, money, gamePhase, runCardAnimation, t]);

  const check = useCallback(() => {
    if (gamePhase === GAME_PHASES.PREFLOP) { setVisibleCommunityCount(3); setGamePhase(GAME_PHASES.FLOP); }
    else if (gamePhase === GAME_PHASES.FLOP) { setVisibleCommunityCount(5); setGamePhase(GAME_PHASES.TURN_RIVER); }
  }, [gamePhase]);

  const fold = useCallback(() => {
    let tripsWin = 0;
    const playerBest = getBestHand(playerHand, communityCards);
    const dealerBest = getBestHand(dealerHand, communityCards);
    
    // Check if it was a good fold (dealer would have won)
    const comparison = compareHands(playerBest, dealerBest);
    const isGoodFold = comparison < 0; // Player would have lost
    const wouldHavePushed = comparison === 0;
    
    if (tripsBet > 0) {
      const tripsPayouts = { 10: 50, 9: 40, 8: 30, 7: 8, 6: 6, 5: 5, 4: 3 };
      if (tripsPayouts[playerBest.rank]) tripsWin = tripsBet + (tripsBet * tripsPayouts[playerBest.rank]);
    }
    if (tripsWin > 0) setMoney(prev => prev + tripsWin);
    
    setStats(prev => {
      const newFolds = prev.folds + 1;
      const newGoodFolds = (prev.goodFolds || 0) + (isGoodFold ? 1 : 0);
      const newHandsPlayed = prev.handsPlayed + 1;
      return { ...prev, folds: newFolds, goodFolds: newGoodFolds, handsPlayed: newHandsPlayed,
        foldPercent: Math.round((newFolds / newHandsPlayed) * 100),
        winPercent: Math.round((prev.wins / newHandsPlayed) * 100),
        losePercent: Math.round((prev.losses / newHandsPlayed) * 100),
        winnings: prev.winnings - anteBet - blindBet + (tripsWin > 0 ? tripsWin - tripsBet : -tripsBet),
      };
    });
    setHandsPlayed(prev => prev + 1);
    setVisibleCommunityCount(5);
    setShowDealerCardsAnim(true);
    
    setGameResults({
      outcome: 'fold', 
      playerHand: playerBest,
      dealerHand: dealerBest,
      isGoodFold,
      wouldHavePushed,
      ante: { result: 'FOLD' }, blind: { result: 'FOLD' }, play: { result: '-' },
      trips: { result: tripsWin > 0 ? 'WIN' : (tripsBet > 0 ? 'LOSE' : '-') },
      netWin: tripsWin - anteBet - blindBet - tripsBet,
    });
    setShowResults(true);
    setGamePhase(GAME_PHASES.SHOWDOWN);
  }, [anteBet, blindBet, tripsBet, playerHand, dealerHand, communityCards]);

  // Helper to get hand type key for stats
  const getHandTypeKey = (rank) => {
    const keys = { 10: 'royalFlush', 9: 'straightFlush', 8: 'fourKind', 7: 'fullHouse', 6: 'flush', 5: 'straight', 4: 'threeKind', 3: 'twoPair', 2: 'pair', 1: 'highCard' };
    return keys[rank] || 'highCard';
  };

  const resolveHand = useCallback(() => {
    const results = calculateHandResults(playerHand, dealerHand, communityCards, anteBet, blindBet, playBet, tripsBet);
    setMoney(prev => prev + results.totalPayout);
    
    const handTypeKey = getHandTypeKey(results.playerHand?.rank);
    const betTypeKey = currentBetType ? `bets${currentBetType.replace('x', 'x')}` : 'bets1x';
    
    setStats(prev => {
      let newWins = prev.wins, newLosses = prev.losses;
      const updates = {};
      
      if (results.outcome === 'win') {
        newWins++;
        updates[`${handTypeKey}Wins`] = (prev[`${handTypeKey}Wins`] || 0) + 1;
        if (currentBetType) updates[`wins${currentBetType.replace('x', 'x')}`] = (prev[`wins${currentBetType.replace('x', 'x')}`] || 0) + 1;
      } else if (results.outcome === 'lose') {
        newLosses++;
        updates[`${handTypeKey}Losses`] = (prev[`${handTypeKey}Losses`] || 0) + 1;
        if (currentBetType) updates[`losses${currentBetType.replace('x', 'x')}`] = (prev[`losses${currentBetType.replace('x', 'x')}`] || 0) + 1;
      }
      
      // Track bet type count
      if (currentBetType) {
        const betKey = `bets${currentBetType.replace('x', 'x')}`;
        updates[betKey] = (prev[betKey] || 0) + 1;
      }
      
      // Track Trips wins
      if (results.trips?.payout > 0) {
        updates.tripsWins = (prev.tripsWins || 0) + 1;
        updates.tripsWinnings = (prev.tripsWinnings || 0) + results.trips.payout;
      }
      
      // Track Blind bonuses
      if (results.blind?.bonus > 0) {
        updates.blindBonuses = (prev.blindBonuses || 0) + 1;
        updates.blindBonusWinnings = (prev.blindBonusWinnings || 0) + results.blind.bonus;
      }
      
      // Update best hand
      const bestHandRank = prev.bestHand ? (prev.bestHandRank || 0) : 0;
      if (results.playerHand?.rank > bestHandRank) {
        updates.bestHand = results.playerHand?.detailedName || results.playerHand?.name;
        updates.bestHandRank = results.playerHand?.rank;
      }
      
      const newHandsPlayed = prev.handsPlayed + 1;
      return { 
        ...prev, 
        ...updates,
        wins: newWins, 
        losses: newLosses, 
        handsPlayed: newHandsPlayed,
        winPercent: Math.round((newWins / newHandsPlayed) * 100),
        losePercent: Math.round((newLosses / newHandsPlayed) * 100),
        foldPercent: Math.round((prev.folds / newHandsPlayed) * 100),
        winnings: prev.winnings + results.netWin,
      };
    });
    setHandsPlayed(prev => prev + 1);
    setGameResults(results);
    setShowResults(true);
    setCurrentBetType(null);
  }, [playerHand, dealerHand, communityCards, anteBet, blindBet, playBet, tripsBet, currentBetType]);

  useEffect(() => {
    if (gamePhase === GAME_PHASES.SHOWDOWN && !showResults && !isAnimating && gameResults?.outcome !== 'fold') {
      resolveHand();
    }
  }, [gamePhase, showResults, isAnimating, resolveHand, gameResults]);

  const newHand = () => {
    setCommunityCards([]); setPlayerHand([]); setDealerHand([]);
    setPlayBet(0); setHasRaised(false); setVisibleCommunityCount(0);
    setShowDealerCardsAnim(false); setIsAnimating(false); setAnimationMessage('');
    setGamePhase(GAME_PHASES.BETTING); setShowResults(false); setGameResults(null);
    setCurrentBetType(null);
  };

  const getVisibleCommunityCards = () => {
    if (gamePhase === GAME_PHASES.BETTING || gamePhase === GAME_PHASES.PREFLOP) return [null, null, null, null, null];
    return communityCards.map((c, i) => i < visibleCommunityCount ? c : null);
  };

  const openAuth = (mode) => { setAuthMode(mode); setShowAuth(true); };

  const isPlaying = gamePhase !== GAME_PHASES.BETTING;
  const canBet = gamePhase === GAME_PHASES.BETTING;
  const showPlayerCards = gamePhase !== GAME_PHASES.BETTING;
  const showDealerCards = gamePhase === GAME_PHASES.SHOWDOWN || showDealerCardsAnim;
  const isAnimationPhase = [GAME_PHASES.ANIMATING_FLOP, GAME_PHASES.ANIMATING_TURN, GAME_PHASES.ANIMATING_RIVER, GAME_PHASES.ANIMATING_DEALER].includes(gamePhase);

  const getAdvice = () => {
    if (!enableAdvice || !showPlayerCards || isAnimationPhase) return null;
    const visibleCards = getVisibleCommunityCards().filter(c => c);
    const playerBest = getBestHand(playerHand, visibleCards);
    
    if (gamePhase === GAME_PHASES.PREFLOP) {
      const val1 = playerHand[0]?.slice(0, -1), val2 = playerHand[1]?.slice(0, -1);
      const suited = playerHand[0]?.slice(-1) === playerHand[1]?.slice(-1);
      if (val1 === val2) return ['A', 'K', 'Q', 'J', '10'].includes(val1) ? `🔥 ${t('strongPair')}` : `💪 ${t('mediumPair')}`;
      if (val1 === 'A' || val2 === 'A') {
        if (suited) return `🔥 ${t('aceSuited')}`;
        const other = val1 === 'A' ? val2 : val1;
        return ['K', 'Q', 'J'].includes(other) ? `💪 ${t('strongAce')}` : `👀 ${t('aceHigh')}`;
      }
      if ((val1 === 'K' || val2 === 'K') && suited) return `💪 ${t('kingSuited')}`;
      return `😐 ${t('weakHand')}`;
    }
    if (gamePhase === GAME_PHASES.FLOP) {
      if (playerBest.rank >= 4) return `🔥 ${t('strongHand')}`;
      if (playerBest.rank >= 2) return `👀 ${t('madeHand')}`;
      return `😐 ${t('weakCheck')}`;
    }
    if (gamePhase === GAME_PHASES.TURN_RIVER) {
      if (playerBest.rank >= 2) return `💪 ${t('madeHandBet')}`;
      return `😰 ${t('considerFolding')}`;
    }
    return null;
  };

  return (
    <div className="min-h-screen" style={{ background: 'radial-gradient(ellipse at center, #1a472a 0%, #0d2818 50%, #061210 100%)' }}>
      {/* Background pattern */}
      <div className="fixed inset-0 opacity-5 pointer-events-none" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffd700' fill-opacity='0.4'%3E%3Cpath d='M20 20v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }}></div>

      <Header 
        onSignUp={() => openAuth('signup')} 
        onLogin={() => openAuth('login')} 
        money={money}
        onMenuOpen={() => setShowMobileMenu(true)}
      />

      {/* Game Explanation Banner */}
      <div className="px-3 md:px-8 py-2 md:py-3" style={{ background: 'linear-gradient(90deg, rgba(26,26,46,0.9), rgba(22,33,62,0.8), rgba(26,26,46,0.9))' }}>
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs md:text-sm" style={{ color: '#ccc' }}>
            <span className="font-bold mr-2" style={{ color: '#ffd700' }}>🎴 {t('howToPlay')}:</span>
            {t('gameExplanation')}
          </p>
        </div>
      </div>
      
      <div className="flex flex-col md:flex-row relative z-10">
        {/* Desktop Sidebar */}
        <div className="hidden md:block w-56 p-4">
          <MoneyPanel money={money} onReset={() => setShowResetMoney(true)} handsPlayed={handsPlayed} onMoreStats={() => setShowStats(true)} />
          <div className="mt-4">
            <PayoutTable title={`💎 ${t('tripsPayout')}`} payouts={TRIPS_PAYOUT} />
            <PayoutTable title={`🎯 ${t('blindPayout')}`} payouts={BLIND_PAYOUT} />
          </div>
        </div>

        {/* Main Game Area */}
        <div className="flex-1 p-2 md:p-4">
          {/* Felt Table */}
          <div 
            className="rounded-2xl md:rounded-3xl p-3 md:p-6 relative overflow-hidden"
            style={{
              background: 'radial-gradient(ellipse at center, #1a5c3a 0%, #0d3d24 100%)',
              border: '4px md:border-8 solid #8B4513',
              boxShadow: 'inset 0 0 30px rgba(0,0,0,0.5), 0 6px 20px rgba(0,0,0,0.5)'
            }}
          >
            <div className="absolute inset-0 rounded-xl md:rounded-2xl pointer-events-none" style={{ boxShadow: 'inset 0 0 60px rgba(255,215,0,0.1)' }}></div>

            {/* Community Cards */}
            <div className="text-center mb-3 md:mb-6">
              <div className="flex justify-center gap-1 md:gap-3 mb-2">
                {getVisibleCommunityCards().map((card, index) => (
                  <Card key={index} card={card} hidden={!card} mobile />
                ))}
              </div>
              <span className="text-[10px] md:text-sm font-bold tracking-wider px-3 py-1 rounded-full" 
                style={{ background: 'rgba(0,0,0,0.3)', color: '#ffd700' }}>
                {t('communityCards')}
              </span>
            </div>

            {/* Animation Message - Fixed at top */}
            {animationMessage && (
              <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 pointer-events-none">
                <div className="px-8 md:px-16 py-3 md:py-5 rounded-2xl animate-bounce"
                  style={{ 
                    background: 'linear-gradient(145deg, rgba(0,0,0,0.95), rgba(26,26,46,0.95))', 
                    border: '3px solid #ffd700', 
                    boxShadow: '0 0 40px rgba(255,215,0,0.6), 0 0 80px rgba(255,215,0,0.3)' 
                  }}>
                  <span className="text-3xl md:text-6xl font-black tracking-widest"
                    style={{ background: 'linear-gradient(180deg, #ffd700, #ff8c00)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                    {animationMessage}
                  </span>
                </div>
              </div>
            )}

            {/* Welcome Message - Mobile optimized */}
            {canBet && (
              <div className="text-center mb-3 md:mb-6">
                <p className="text-sm md:text-xl font-bold" style={{ color: '#ffd700' }}>♠ {t('placeBets')} ♠</p>
                <p className="text-[10px] md:text-sm mt-0.5" style={{ color: '#aaa' }}>{t('antePushes')}</p>
              </div>
            )}

            {/* Advice - Mobile */}
            {enableAdvice && getAdvice() && (
              <div className="text-center mb-2 md:mb-4">
                <div className="inline-block px-3 md:px-6 py-1 md:py-2 rounded-full animate-pulse text-xs md:text-base"
                  style={{ background: 'linear-gradient(145deg, rgba(255,215,0,0.2), rgba(255,140,0,0.2))', border: '1px md:border-2 solid #ffd700' }}>
                  <span className="font-bold" style={{ color: '#ffd700' }}>{getAdvice()}</span>
                </div>
              </div>
            )}

            {/* Results Display */}
            {showResults && gameResults && (
              <div className="text-center mb-3 md:mb-6">
                <div className={`inline-block px-4 md:px-8 py-2 md:py-4 rounded-xl md:rounded-2xl ${gameResults.outcome === 'win' ? 'animate-bounce' : ''}`}
                  style={{
                    background: gameResults.outcome === 'win' ? 'linear-gradient(145deg, #00ff88, #00cc6a)' : 
                      gameResults.outcome === 'push' ? 'linear-gradient(145deg, #ffd700, #b8860b)' : 
                      (gameResults.outcome === 'fold' && gameResults.isGoodFold) ? 'linear-gradient(145deg, #3b82f6, #1d4ed8)' :
                      'linear-gradient(145deg, #dc2626, #991b1b)'
                  }}>
                  <div className="text-lg md:text-3xl font-black" style={{ color: '#1a1a2e' }}>
                    {gameResults.outcome === 'win' ? `🎉 ${t('winner')} 🎉` : 
                     gameResults.outcome === 'fold' ? (
                       gameResults.isGoodFold ? `✓ ${t('goodFold')}` : 
                       gameResults.wouldHavePushed ? `≈ ${t('foldPush')}` :
                       `✗ ${t('badFold')}`
                     ) :
                     gameResults.outcome === 'push' ? t('push') : t('dealerWins')}
                  </div>
                  <div className="text-sm md:text-xl font-bold" style={{ color: 'rgba(0,0,0,0.7)' }}>
                    {gameResults.netWin >= 0 ? '+' : ''}{gameResults.netWin}
                  </div>
                  {gameResults.outcome === 'fold' && (
                    <div className="text-[10px] md:text-xs mt-1" style={{ color: 'rgba(0,0,0,0.6)' }}>
                      {gameResults.isGoodFold ? t('dealerWouldWin') : 
                       gameResults.wouldHavePushed ? t('wouldHaveTied') :
                       t('youWouldWin')}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Hands Display - Mobile stacked */}
            <div className="flex justify-center gap-8 md:gap-24 mb-3 md:mb-6">
              {/* Dealer Hand */}
              <div className="text-center">
                <div className="inline-block px-2 md:px-4 py-0.5 md:py-1 rounded-full mb-1 md:mb-3"
                  style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,215,0,0.3)' }}>
                  <span className="text-[10px] md:text-xs font-bold" style={{ color: '#ffd700' }}>🎩 {t('dealerHand')}</span>
                </div>
                <div className="flex gap-1 md:gap-2 justify-center">
                  <Card card={dealerHand[0]} hidden={!showDealerCards} mobile />
                  <Card card={dealerHand[1]} hidden={!showDealerCards} mobile />
                </div>
                {showDealerCards && gameResults && (
                  <div className="mt-1 text-[10px] md:text-sm font-bold" style={{ color: '#ffd700' }}>
                    {gameResults.dealerHand?.detailedName || gameResults.dealerHand?.name}
                  </div>
                )}
              </div>

              {/* Player Hand */}
              <div className="text-center">
                <div className="inline-block px-2 md:px-4 py-0.5 md:py-1 rounded-full mb-1 md:mb-3"
                  style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,255,136,0.3)' }}>
                  <span className="text-[10px] md:text-xs font-bold" style={{ color: '#00ff88' }}>👤 {t('yourHand')}</span>
                </div>
                <div className="flex gap-1 md:gap-2 justify-center">
                  <Card card={showPlayerCards ? playerHand[0] : null} hidden={!showPlayerCards} mobile />
                  <Card card={showPlayerCards ? playerHand[1] : null} hidden={!showPlayerCards} mobile />
                </div>
                {showPlayerCards && !isAnimationPhase && (
                  <div className="mt-1 text-[10px] md:text-sm font-bold" style={{ color: '#00ff88' }}>
                    {gamePhase === GAME_PHASES.PREFLOP 
                      ? (evaluatePreflopHand(playerHand).detailedName || evaluatePreflopHand(playerHand).name)
                      : (gamePhase === GAME_PHASES.SHOWDOWN && gameResults?.playerHand?.detailedName) 
                        ? gameResults.playerHand.detailedName 
                        : getBestHand(playerHand, getVisibleCommunityCards().filter(c => c)).detailedName || getBestHand(playerHand, getVisibleCommunityCards().filter(c => c)).name}
                  </div>
                )}
              </div>
            </div>

            {/* Betting Chips - Mobile: 2 rows */}
            <div className="mb-3 md:mb-4">
              {/* Chips Row */}
              <div className="flex justify-center items-center gap-2 md:gap-4 mb-2 md:mb-4 flex-wrap">
                <MobileChip label={t('trips')} value={tripsBet} onChange={setTripsBet} color="purple" disabled={isPlaying} result={showResults ? gameResults?.trips?.result : null} size="small" t={t} />
                <MobileChip label={t('ante')} value={anteBet} onChange={(v) => { setAnteBet(v); setBlindBet(v); }} color="red" disabled={isPlaying} result={showResults ? gameResults?.ante?.result : null} size="small" t={t} />
                <span className="text-lg md:text-3xl font-bold" style={{ color: '#ffd700' }}>=</span>
                <MobileChip label={t('blind')} value={blindBet} color="blue" disabled={true} result={showResults ? gameResults?.blind?.result : null} size="small" t={t} />
                <MobileChip label={t('play')} value={playBet} color="green" disabled={true} result={showResults ? gameResults?.play?.result : null} size="small" t={t} />
              </div>

              {/* Action Buttons */}
              <div className="flex justify-center">
                {canBet && (
                  <button onClick={dealCards}
                    className="px-8 md:px-12 py-3 md:py-4 rounded-xl font-black text-lg md:text-2xl transition-all active:scale-95"
                    style={{ background: 'linear-gradient(145deg, #ffd700, #b8860b)', color: '#1a1a2e', boxShadow: '0 4px 20px rgba(255,215,0,0.5)' }}>
                    🃏 {t('deal')}
                  </button>
                )}

                {gamePhase === GAME_PHASES.PREFLOP && !hasRaised && (
                  <div className="flex gap-2">
                    <MobileButton onClick={check} variant="secondary">{t('check')}</MobileButton>
                    <MobileButton onClick={() => makePlayBet(3)} variant="primary">3X</MobileButton>
                    <MobileButton onClick={() => makePlayBet(4)} variant="primary">4X</MobileButton>
                  </div>
                )}

                {gamePhase === GAME_PHASES.FLOP && !hasRaised && (
                  <div className="flex gap-2">
                    <MobileButton onClick={check} variant="secondary">{t('check')}</MobileButton>
                    <MobileButton onClick={() => makePlayBet(2)} variant="primary">2X</MobileButton>
                  </div>
                )}

                {gamePhase === GAME_PHASES.TURN_RIVER && !hasRaised && (
                  <div className="flex gap-2">
                    <MobileButton onClick={fold} variant="danger">{t('fold')}</MobileButton>
                    <MobileButton onClick={() => makePlayBet(1)} variant="primary">1X</MobileButton>
                  </div>
                )}

                {isAnimationPhase && (
                  <div className="px-4 md:px-8 py-2 md:py-3 rounded-xl font-bold text-sm md:text-base"
                    style={{ background: 'rgba(0,0,0,0.5)', border: '1px solid #ffd700', color: '#ffd700' }}>
                    ✨ {t('revealing')}
                  </div>
                )}

                {showResults && !isAnimationPhase && (
                  <button onClick={newHand}
                    className="px-6 md:px-10 py-2 md:py-3 rounded-xl font-bold text-sm md:text-lg transition-all active:scale-95"
                    style={{ background: 'linear-gradient(145deg, #3b82f6, #1d4ed8)', color: 'white' }}>
                    🔄 {t('newHand')}
                  </button>
                )}
              </div>
            </div>

            {/* Total Bet */}
            {isPlaying && (
              <div className="text-center">
                <span className="px-3 md:px-6 py-1 md:py-2 rounded-full text-xs md:text-lg font-bold"
                  style={{ background: 'rgba(0,0,0,0.3)', color: '#ffd700' }}>
                  💰 {t('totalBet')}: ${getTotalBet()}
                </span>
              </div>
            )}
          </div>

          {/* Settings - Desktop only */}
          <div className="hidden md:block">
            <SettingsPanel enableAdvice={enableAdvice} setEnableAdvice={setEnableAdvice} enableSound={enableSound} setEnableSound={setEnableSound} onChangeDeck={() => setShowChangeDeck(true)} />
          </div>

          {/* Mobile Settings Bar */}
          <div className="md:hidden flex justify-center gap-4 mt-3 py-2">
            <button onClick={() => setEnableAdvice(!enableAdvice)} 
              className={`px-3 py-1.5 rounded-full text-xs font-bold ${enableAdvice ? 'bg-yellow-500/30' : 'bg-white/10'}`}
              style={{ color: enableAdvice ? '#ffd700' : '#888', border: `1px solid ${enableAdvice ? '#ffd700' : '#444'}` }}>
              💡 {t('advice')}
            </button>
            <button onClick={() => setShowStats(true)} 
              className="px-3 py-1.5 rounded-full text-xs font-bold bg-white/10"
              style={{ color: '#888', border: '1px solid #444' }}>
              📊 Stats
            </button>
            <button onClick={() => setShowResetMoney(true)} 
              className="px-3 py-1.5 rounded-full text-xs font-bold bg-white/10"
              style={{ color: '#888', border: '1px solid #444' }}>
              💰 {t('reset')}
            </button>
          </div>

          {/* Recent Wins - Desktop */}
          <div className="hidden md:block">
            <RecentBigWins playersOnline={playersOnline} />
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <MobileMenu 
        isOpen={showMobileMenu} 
        onClose={() => setShowMobileMenu(false)}
        onSignUp={() => { setShowMobileMenu(false); openAuth('signup'); }}
        onLogin={() => { setShowMobileMenu(false); openAuth('login'); }}
        onStats={() => { setShowMobileMenu(false); setShowStats(true); }}
        onReset={() => { setShowMobileMenu(false); setShowResetMoney(true); }}
        stats={stats}
        handsPlayed={handsPlayed}
      />

      {/* Modals */}
      <StatsModal isOpen={showStats} onClose={() => setShowStats(false)} stats={stats} />
      <ResetMoneyModal isOpen={showResetMoney} onClose={() => setShowResetMoney(false)} onReset={(amount) => setMoney(amount)} />
      <ChangeDeckModal isOpen={showChangeDeck} onClose={() => setShowChangeDeck(false)} currentDeck={selectedDeck} onChangeDeck={setSelectedDeck} />
      <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} mode={authMode} />

      {/* SEO Content Section */}
      <SEOContent />

      <style>{`
        @keyframes cardFlip { 0% { transform: rotateY(180deg) scale(0.8); opacity: 0; } 100% { transform: rotateY(0deg) scale(1); opacity: 1; } }
      `}</style>
    </div>
  );
};

// Mobile-optimized button
const MobileButton = ({ onClick, children, variant = 'primary' }) => {
  const styles = {
    primary: { bg: 'linear-gradient(145deg, #16a34a, #15803d)', border: '#22c55e' },
    secondary: { bg: 'linear-gradient(145deg, #4b5563, #374151)', border: '#6b7280' },
    danger: { bg: 'linear-gradient(145deg, #dc2626, #991b1b)', border: '#ef4444' }
  };
  const s = styles[variant];
  
  return (
    <button onClick={onClick}
      className="px-4 md:px-5 py-2 md:py-2.5 rounded-lg font-bold text-white text-sm md:text-base transition-all active:scale-95"
      style={{ background: s.bg, border: `2px solid ${s.border}` }}>
      {children}
    </button>
  );
};

export default GameBoard;
